import { ApiRequestError } from '../lib/api.js';

export interface BulkResult<T> {
  done: number;
  failed: T[];
}

/**
 * The most one run may touch, and the only one of these numbers the employer
 * ever meets.
 *
 * Not unbounded, because the run lives in the page: at roughly a third of a
 * second per item this is over a minute with the dialog open, and closing the
 * tab halfway leaves the rest untouched with nothing server-side recording it.
 * A ceiling that high is one nobody reaches by accident; the fix for the people
 * who do is moving the fan-out behind a BFF route, not a larger number here.
 */
export const BULK_MAX = 200;

/**
 * How many go out before the loop takes a breath. Internal — no dialog names it
 * and no progress bar counts in it. The employer picks a number and watches one
 * bar fill; that this happens in twenties is our business.
 *
 * The chunk earns nothing on its own. Five back-to-back twenties are the same
 * hundred calls at the same rate as one loop of a hundred, and the upstream
 * cannot tell them apart. What earns it is `CHUNK_PAUSE_MS` below: the chunk is
 * a place to pause, and a boundary the run can be reasoned about in.
 */
const CHUNK_SIZE = 20;

/** The breath between chunks. */
const CHUNK_PAUSE_MS = 1000;

/** Waits before re-attempting one item the upstream refused for load. */
const RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

export interface BulkOptions {
  /** Ceiling for this run. Defaults to `BULK_MAX`. */
  cap?: number;
  chunkSize?: number;
  pauseMs?: number;
  retryDelaysMs?: readonly number[];
}

const wait = (ms: number): Promise<void> =>
  ms <= 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether a refusal is the upstream asking us to slow down rather than telling
 * us this item will never work.
 *
 * The three actions fail in two different shapes. The stages and End come back
 * through the BFF envelope as `ApiRequestError`, which already carries the
 * answer — a 429 is added because a rate limit is retryable whatever the route
 * said. Bulk send goes straight to Stream, whose SDK throws its own error with a
 * `status` on it, so that path is duck-typed rather than instance-checked: the
 * shape is what matters, and pinning a class here would break on an SDK bump.
 *
 * Anything else is left alone. Retrying a 400 is three more round trips to be
 * told the same thing, and it delays every conversation queued behind it.
 */
export function isRetryable(error: unknown): boolean {
  if (error instanceof ApiRequestError) return error.retryable || error.status === 429;
  const status: unknown = (error as { status?: unknown } | null)?.status;
  return status === 429 || (typeof status === 'number' && status >= 500);
}

/** One item, re-attempted while the upstream is only asking us to wait. */
async function applyWithRetry<T>(
  apply: (item: T) => Promise<void>,
  item: T,
  delays: readonly number[],
): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await apply(item);
      return;
    } catch (error) {
      if (attempt >= delays.length || !isRetryable(error)) throw error;
      await wait(delays[attempt]!);
    }
  }
}

/**
 * Run one async action across a set of items, sequentially, in paced chunks. A
 * per-item failure is caught and collected, never thrown past the loop, so one
 * dead item doesn't abort the rest. `apply` is injected so the loop stays pure
 * and testable — bulk send passes `(id) => sendMessageTo(id, text)`, a hiring
 * stage passes `(id) => setApplicantStatus(id, stage)`, End passes
 * `(id) => endChat(id, …)`.
 *
 * `onProgress` reports items processed / total as they land, so the compose
 * dialog can show a live bar; it counts failures too, since the bar tracks how
 * far the run has got, not how much of it succeeded. The total it reports is the
 * run's, never a chunk's — the chunking is invisible from out here.
 *
 * ponytail: still browser-bound. The run dies with the tab, and nothing
 * server-side records how far it got. The permanent fix is one BFF route doing
 * the fan-out with the server SDK — see AI-2887's out-of-scope note and AI-2574.
 */
export async function runBulk<T>(
  items: T[],
  apply: (item: T) => Promise<void>,
  onProgress?: (processed: number, total: number) => void,
  options: BulkOptions = {},
): Promise<BulkResult<T>> {
  const {
    cap = BULK_MAX,
    chunkSize = CHUNK_SIZE,
    pauseMs = CHUNK_PAUSE_MS,
    retryDelaysMs = RETRY_DELAYS_MS,
  } = options;

  const batch = items.slice(0, cap);
  let done = 0;
  const failed: T[] = [];

  for (let start = 0; start < batch.length; start += chunkSize) {
    // Between chunks, never before the first or after the last: a pause with no
    // work on either side of it is the employer watching a still bar.
    if (start > 0) await wait(pauseMs);

    for (const item of batch.slice(start, start + chunkSize)) {
      try {
        await applyWithRetry(apply, item, retryDelaysMs);
        done += 1;
      } catch {
        failed.push(item);
      }
      onProgress?.(done + failed.length, batch.length);
    }
  }

  return { done, failed };
}
